/**
 * Copyright © 2025 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React from "react";
import { useNavigate } from "react-router-dom";
import { 
  InputAdornment,
  IconButton,
  DialogContent,
  DialogContentText,
  Divider,
} from "@mui/material";
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import { 
  StyledTitle,
  StyledResetButton,
  StyledEmailField,
  StyledSubTitle,
  StyledPasswordField,
  StyledFormContainer,
  StyledHelperText,
  StyledDialogTitle,
  StyledList,
  StyledDialog,
  StyledErrorTitleContainer,
  ResetPasswordWrapper,
  FormFieldTitle,
  StyledBackToLoginButton,
  StyledInstructions,
  CarouselContainer,
  FormContainer,
  FormWrapper,
  pageContainerStyle,
  errorIconStyle,
  errorDescStyle,
  passwordReqStyle,
  dividerStyle,
  passwordInstructionStyle,
  buttonContainerStyle,
} from "./styles";
import api from "../../api";
import { LoadingSpinner } from "../../components/loading_spinner/index";
import { useNotificationContext } from '../../contexts/notificationContext'
import authManager from "../../utils/authManager";
import { CarouselPage } from "../../components/carousel";
import banner from '../../assets/images/banner.jpg';

export const ResetPasswordFirstLogin = () => {
  const navigate = useNavigate();
  const [data, setData] = React.useState({});
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const [newPassword, setNewPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [error, setError] = React.useState('');
  const [errorOpen, setErrorOpen] = React.useState(false);
  const [status, setStatus] = React.useState(false);
  const [fieldError, setFieldError] = React.useState({
    new_password: '',
    confirm_password: ''
  });
  const showNotification = useNotificationContext();
  const [newPasswordCheck, setNewPasswordCheck] = React.useState(true);
  const [confirmPasswordCheck, setConfirmPasswordCheck] = React.useState(true);
  const carouselData = [
    {
      id: 1,
      image: banner,
    },
    {
      id: 2,
      image: banner,
    },
    {
      id: 3,
      image: banner,
    },
  ];

  React.useEffect(() => {
    api.get('/v1/admin/me')
    .then((response) => {
      setData(response.data)
    })
    .catch((error) => {
      console.error('Failed to fetch admin data:', error);
    });
  }, []);

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleClickShowConfirmPassword = () => setShowConfirmPassword((show) => !show);

  const handleResetPassword = async () => {
    setStatus(true);
    try {
      const response = await api.put('/v1/admin/reset_password', {
        email: data?.email,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });

      const authenticatedAdmin = response?.data?.admin || {
        ...data,
        password_changed: true,
      };

      authManager.login(authenticatedAdmin, {
        authToken: response?.data?.token,
      });
      showNotification('Password updated successfully!', 'success');
      setTimeout(() => {
        navigate('/admin/dashboard');
      }, 1500);
    } catch (error) {
      const responseErrors = error?.response?.data?.errors || {};
      const fields = Object.keys(responseErrors);

      if (error?.response?.status === 401) {
        showNotification('Your reset password session is invalid. Please sign in again.', 'error');
      } else if (fields[0] && fields[0] !== 'base') {
        setFieldError(responseErrors);
      } else {
        const issue = responseErrors?.base?.[0] || error?.response?.data?.error;
        if (issue) {
          setError(issue);
          setErrorOpen(true);
        }
      }
    } finally {
      setStatus(false);
    }
  };

  const handleNewPassword = (value) => {
    setNewPassword(value);
    if (fieldError?.new_password) {
      setFieldError({...fieldError, new_password: ''});
    }
    if (value) {
      setNewPasswordCheck(false);
    }
    else {
      setNewPasswordCheck(true);
    }
  };

  const handleConfirmPassword = (value) => {
    setConfirmPassword(value);
    if (fieldError?.confirm_password) {
      setFieldError({...fieldError, confirm_password: ''});
    }
    if (value) {
      setConfirmPasswordCheck(false);
    }
    else {
      setConfirmPasswordCheck(true);
    }
  };

  const handleClose = () => {
    setErrorOpen(false);
    setError('');
  };

  const handleCancel = async () => {
    if (authManager.isAuthenticated()) {
      await authManager.logout(true);
    }

    navigate('/', { replace: true });
  }

  return (
    <React.Fragment>
      {/* Error Messages */}
      <StyledDialog
        open={errorOpen}
        onClose={handleClose}
      >
        <StyledErrorTitleContainer>
          <ReportProblemIcon style={errorIconStyle}/>
          <StyledDialogTitle>Error for password</StyledDialogTitle>
        </StyledErrorTitleContainer>

        <DialogContent>
          <DialogContentText sx={errorDescStyle}>{error}</DialogContentText>
          <DialogContentText sx={passwordReqStyle}>
            Password must:
          </DialogContentText>
          <StyledList>
            <li>have at least 8 characters</li>
            <li>have a capital letter</li>
            <li>have a small letter</li>
            <li>have a number</li>
          </StyledList>
        </DialogContent>
      </StyledDialog>

      <div style={pageContainerStyle}>
        <ResetPasswordWrapper>
          <CarouselContainer>
            <CarouselPage data={carouselData} />
          </CarouselContainer>
          
          <FormContainer>
            <FormWrapper>
              <StyledTitle>Reset Password</StyledTitle>
              <StyledSubTitle>Your new password must be different from previously used passwords.</StyledSubTitle>

              <Divider style={dividerStyle}/>

              <StyledFormContainer>
                <div>
                  <FormFieldTitle>Email</FormFieldTitle>
                  <StyledEmailField
                    value={data?.email}
                    fullWidth
                  />
                </div>

                <div>
                  <FormFieldTitle>Create new password</FormFieldTitle>
                  <StyledPasswordField
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter new password"
                    onChange={(e) => handleNewPassword(e.target.value)}
                    error={fieldError?.new_password}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleClickShowPassword}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                  <StyledHelperText>
                    {fieldError?.new_password && `${fieldError?.new_password}`}
                  </StyledHelperText>
                </div>

                <div>
                  <FormFieldTitle>Confirm new password</FormFieldTitle>
                  <StyledPasswordField
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Confirm new password"
                    onChange={(e) => handleConfirmPassword(e.target.value)}
                    error={fieldError?.confirm_password}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleClickShowConfirmPassword}
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                  <StyledHelperText>
                    {fieldError?.confirm_password && `${fieldError?.confirm_password}`}
                  </StyledHelperText>
                </div>
                <div>
                  <p style={passwordInstructionStyle}>Password must:</p>
                  <StyledInstructions>
                    <li>have at least 8 characters</li>
                    <li>have atleast 1 number</li>
                    <li>have atleast 1 letter</li>
                    <li>contain no spaces</li>
                  </StyledInstructions>
                </div>
                <div style={buttonContainerStyle}>
                  <StyledBackToLoginButton 
                    variant="outlined"
                    onClick={handleCancel}
                  >
                    Cancel
                  </StyledBackToLoginButton>
                  <StyledResetButton 
                    variant="contained"
                    onClick={handleResetPassword}
                    disabled={status || (newPasswordCheck || confirmPasswordCheck)}
                  >
                    { status ? <LoadingSpinner isLoading={true} />: 'Reset Password' }
                  </StyledResetButton>
                </div>
            </StyledFormContainer>
            </FormWrapper>
          </FormContainer>
        </ResetPasswordWrapper>
      </div>
    </React.Fragment>
  )
}
